'use client';

import type { ReactNode } from 'react';

import Image from 'next/image';

import { useTranslation } from 'react-i18next';

import { isInternalLandingHref, isSafeLandingFooterHref } from '../../_lib/landing-footer-links';
import type { LandingNav, LandingTheme } from '../../_lib/schema/landing-config.schema';
import { pickLocalized } from './landing-context';
import { contrastForeground, landingThemeStyle } from './landing-design';
import { LandingFooterLink as FooterLink } from './landing-footer-link';
import { StudioSiteFooter } from './studio-site-footer';
import styles from './website-builder-footer.module.css';

type WebsiteBuilderFooterProps = {
  nav: LandingNav;
  businessName?: string;
  logoUrl?: string | null;
  locale?: string;
  disableNavigation?: boolean;
  contact?: {
    address?: string | null;
    city?: string | null;
    country?: string | null;
    phone?: string | null;
    email?: string | null;
  } | null;
  legalControls?: ReactNode;
  fontFamily?: LandingTheme['fontFamily'];
  theme?: LandingTheme;
};

export function WebsiteBuilderFooter({ nav, businessName, logoUrl, locale, disableNavigation, contact, legalControls, fontFamily, theme }: WebsiteBuilderFooterProps) {
  const { t, i18n } = useTranslation();
  const language = locale ?? i18n.language;
  const footer = nav.footer;

  if (footer?.layout === 'studio') return <StudioSiteFooter nav={nav} theme={theme} businessName={businessName} logoUrl={logoUrl} locale={locale} fontFamily={fontFamily ?? theme?.fontFamily} legalControls={legalControls} disableNavigation={disableNavigation} />;

  if (!footer || (footer.layout !== 'centered' && footer.layout !== 'columns')) return null;

  const translate = (key: string) => t(key, { lng: language });
  const brand = nav.brand?.wordmark || businessName || nav.logo?.alt;
  const description = pickLocalized(nav.brand?.footerText, nav.brand?.footerText_ar, language);
  const motto = pickLocalized(nav.brand?.motto, nav.brand?.motto_ar, language);
  const copyright = pickLocalized(nav.brand?.copyright, nav.brand?.copyright_ar, language);
  const linksHeading = pickLocalized(footer.linksHeading, footer.linksHeading_ar, language) || translate('marketing:sportsChrome.explore');
  const links = nav.footerLinks
    ? nav.footerLinks.filter((link) => isSafeLandingFooterHref(link.href))
    : nav.links.flatMap((link) => [link, ...(link.children ?? [])]).filter((link) => isInternalLandingHref(link.href));
  const cityCountry = [contact?.city, contact?.country].filter(Boolean).join(', ');
  const typography = landingThemeStyle({ ...theme, fontFamily: fontFamily ?? theme?.fontFamily });
  const showBrand = footer.showLogo !== false || (footer.showDescription !== false && Boolean(description || motto));

  return (
    <footer
      className={styles.footer}
      data-test="website-builder-footer"
      data-layout={footer.layout}
      data-font-family={fontFamily}
      dir={language?.split('-')[0] === 'ar' ? 'rtl' : 'ltr'}
      style={{
        ...typography,
        backgroundColor: footer.backgroundColor ?? typography.backgroundColor,
        color: footer.textColor ?? (footer.backgroundColor ? contrastForeground(footer.backgroundColor) : typography.color),
      }}
    >
      <div className={styles.inner}>
        <div className={styles.content}>
          {showBrand && <div className={styles.brand}>
            {footer.showLogo !== false && <div className={styles.identity}>
              <FooterLink href="/" disabled={disableNavigation}>
                {logoUrl ? <Image src={logoUrl} alt={nav.logo?.alt || brand || ''} width={220} height={80} unoptimized className={styles.logo} /> : brand}
              </FooterLink>
            </div>}
            {footer.showDescription !== false && <>
              {description && <p className={styles.copy}>{description}</p>}
              {motto && <p className={styles.copy}>{motto}</p>}
            </>}
          </div>}
          {footer.showNavigation !== false && links.length > 0 && <nav className={styles.navigation} aria-label={linksHeading}>
            <h2 className={styles.heading}>{linksHeading}</h2>
            <ul className={styles.links}>
              {links.map((link, index) => <li key={`${link.href}-${index}`}>
                <FooterLink href={link.href} disabled={disableNavigation}>{pickLocalized(link.label, link.label_ar, language)}</FooterLink>
              </li>)}
            </ul>
          </nav>}
          {footer.showContact !== false && <div className={styles.contact}>
            <h2 className={styles.heading}>{translate('marketing:footer.contactUs')}</h2>
            <address>
              {contact?.address && <p>{contact.address}</p>}
              {cityCountry && <p>{cityCountry}</p>}
              {contact?.phone && <p><bdi>{contact.phone}</bdi></p>}
              {contact?.email && <p><bdi>{contact.email}</bdi></p>}
            </address>
            <FooterLink href="/contact" disabled={disableNavigation}>{translate('marketing:footer.contactUs')}</FooterLink>
          </div>}
        </div>
        <div className={styles.bottom}>
          <p className={styles.copy}>© {new Date().getFullYear()} {copyright || brand}</p>
          <nav className={styles.legal} aria-label={translate('marketing:legal.label')}>
            <FooterLink href="/privacy-policy" disabled={disableNavigation}>{translate('marketing:privacyPolicy')}</FooterLink>
            <FooterLink href="/terms-of-service" disabled={disableNavigation}>{translate('marketing:termsOfService')}</FooterLink>
            <FooterLink href="/cookie-policy" disabled={disableNavigation}>{translate('marketing:cookiePolicy')}</FooterLink>
            {legalControls}
          </nav>
          <p>{translate('marketing:footer.poweredBy')}</p>
        </div>
      </div>
    </footer>
  );
}
